import type { ClientChatMessage } from '@DigitaleDoerfer/digitale-doerfer-api';
import { useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetPostById } from '@/features/api/hooks/grapevine-post/useGetPostById';
import { DeletedRefChatMessage } from '@/features/chats/components/DeletedRefChatMessage';
import { LoadingRefChatMessage } from '@/features/chats/components/LoadingRefChatMessage';
import { RefChatMessage } from '@/features/chats/components/RefChatMessage';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { AppRoute } from '@/navigation/types/navigation';
import { dayjs } from '@/time';

type Props = {
  message: ClientChatMessage;
};

export const PostRefChatMessage: FC<Props> = ({ message }) => {
  const { t } = useTranslation(LocalizationNameSpace.CHATS, {
    keyPrefix: 'screens.chat.message'
  });

  const hasRefData =
    message.referencedEntityId != undefined &&
    message.referencedEntityCreated != undefined &&
    message.referencedEntityDeleted != undefined;

  const deleted = message.referencedEntityDeleted === true;

  const requiresFetch = !hasRefData && !deleted;

  const { data: post, isFetching } = useGetPostById(
    requiresFetch ? message.referencedEntityId : undefined
  );

  const id = message.referencedEntityId;
  const created = message.referencedEntityCreated ?? post?.created;
  const date = useMemo(() => dayjs(created).format('L'), [created]);
  const text = message.referencedEntityText ?? post?.text;

  if (isFetching) {
    return <LoadingRefChatMessage />;
  }

  if (deleted || (requiresFetch && !post)) {
    return <DeletedRefChatMessage />;
  }

  return (
    <RefChatMessage
      screen={AppRoute.PostDetail}
      params={{ id }}
      header={t('relatedToPost', { date })}
      content={text}
    />
  );
};
